import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Github, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { githubAuthApi } from '@/api/github';

export function GitHubCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setStatus('error');
      setError(errorDescription || 'GitHub authorization was denied');
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received from GitHub');
      return;
    }

    // Verify state matches (CSRF protection)
    const storedState = sessionStorage.getItem('github_oauth_state');
    if (storedState && state !== storedState) {
      setStatus('error');
      setError('Invalid state parameter. Please try connecting again.');
      return;
    }

    // Complete the connection
    async function completeConnection() {
      try {
        await githubAuthApi.completeConnection(code!, state || undefined);
        sessionStorage.removeItem('github_oauth_state');
        setStatus('success');

        // Redirect after a short delay
        setTimeout(() => {
          navigate('/settings/github', { replace: true });
        }, 1500);
      } catch (err: unknown) {
        setStatus('error');
        const errorMessage = err instanceof Error ? err.message : 'Failed to connect GitHub account';
        setError(errorMessage);
      }
    }

    completeConnection();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-dark-800 rounded-xl shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
              <Github className="w-8 h-8 text-gray-600 dark:text-gray-300" />
            </div>
          </div>

          {status === 'loading' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Connecting GitHub
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Please wait while we complete the connection...
              </p>
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-cyan-500" />
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                GitHub Connected!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Redirecting to settings...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {error}
              </p>
              <button
                onClick={() => navigate('/settings/github')}
                className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                Back to Settings
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
